import { useEffect, useMemo, useRef } from 'react';
import { prefersReducedMotion } from '../utility/motion';

export function Loader({visible, progress}) {
    return (
        <div className={`loader ${visible ? '' : 'is-hidden'}`} aria-hidden={!visible}>
            <div className="loader__mark">
                <span className="loader__letter">F</span>
                <span className="loader__letter">C</span>
            </div>
            <div className="loader__bar"><span style={{ transform: `scaleX(${progress / 100})` }} /></div>
            <span className="loader__value mono">{progress}%</span>
        </div>
    );
}

export function Backdrop() {
    return (
        <div className="backdrop" aria-hidden="true">
            <span className="backdrop__blob backdrop__blob--1" />
            <span className="backdrop__blob backdrop__blob--2" />
            <span className="backdrop__blob backdrop__blob--3" />
            <span className="backdrop__grid" />
            <span className="backdrop__noise" />
        </div>
    );
}

// Soft light that follows the pointer on devices with a precise cursor.
export function CursorGlow() {
    const ref = useRef(null);

    useEffect(() => {
        const node = ref.current;
        if (!node || typeof window.matchMedia !== 'function' || prefersReducedMotion()) return undefined;
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return undefined;

        let frame = 0;
        let x = window.innerWidth / 2;
        let y = window.innerHeight / 3;
        let currentX = x;
        let currentY = y;

        const handleMove = (event) => {
            x = event.clientX;
            y = event.clientY;
            node.classList.add('is-active');
        };
        const tick = () => {
            currentX += (x - currentX) * 0.12;
            currentY += (y - currentY) * 0.12;
            node.style.transform = `translate3d(${currentX}px, ${currentY}px, 0)`;
            frame = requestAnimationFrame(tick);
        };

        window.addEventListener('pointermove', handleMove, { passive: true });
        frame = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('pointermove', handleMove);
        };
    }, []);

    return <div className="cursor-glow" ref={ref} aria-hidden="true" />;
}

export function ScrollProgress() {
    const ref = useRef(null);

    useEffect(() => {
        let frame = 0;
        const update = () => {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            const value = max > 0 ? window.scrollY / max : 0;
            if (ref.current) ref.current.style.transform = `scaleX(${value})`;
        };
        const onScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        };
        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    return <div className="scroll-progress" aria-hidden="true"><span ref={ref} /></div>;
}

export function Marquee({items}) {
    const loop = [...items, ...items];
    return (
        <div className="marquee" aria-hidden="true">
            <div className="marquee__track">
                {loop.map((item, index) => (
                    <span className="marquee__item" key={`${item}-${index}`}>
                        {item}
                        <span className="marquee__star">✦</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

export function MatrixRain({active, checked}) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!active || !canvasRef.current) return undefined;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return undefined;
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEF';
        const fontSize = 14;
        const columns = Math.floor(canvas.width / fontSize);
        const drops = Array(columns).fill(1);

        const draw = () => {
            ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#0F0';
            ctx.font = `${fontSize}px monospace`;

            for (let i = 0; i < drops.length; i++) {
                const text = chars[Math.floor(Math.random() * chars.length)];
                ctx.fillText(text, i * fontSize, drops[i] * fontSize);
                if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
                    drops[i] = 0;
                }
                drops[i]++;
            }
        };

        const interval = setInterval(draw, 35);
        return () => clearInterval(interval);
    }, [active]);

    if (!active) return null;
    return (
        <div className="matrix">
            <canvas ref={canvasRef} />
            <p className="matrix__text">{checked ? 'Hai trovato un segreto! 🎮' : 'You found a secret! 🎮'}</p>
        </div>
    );
}

export function HeartShower({active}) {
    const hearts = useMemo(
        () => Array.from({ length: 50 }).map(() => ({
            left: `${Math.random() * 100}%`,
            size: `${Math.random() * 10 + 14}px`,
            duration: `${2 + Math.random() * 2}s`,
            delay: `${Math.random() * 2}s`,
            emoji: ['❤️', '💜', '💙', '🩷', '💗'][Math.floor(Math.random() * 5)],
        })),
        [active] // eslint-disable-line react-hooks/exhaustive-deps
    );

    if (!active) return null;
    return (
        <div className="hearts">
            {hearts.map((heart, index) => (
                <span
                    key={index}
                    className="hearts__item"
                    style={{
                        left: heart.left,
                        fontSize: heart.size,
                        animationDuration: heart.duration,
                        animationDelay: heart.delay,
                    }}
                >
                    {heart.emoji}
                </span>
            ))}
        </div>
    );
}
